import type {CodeNavigationInfo, CodeSection, CodeSectionLookup, DefinitionOrReference} from '@github-ui/code-nav'
import {DuplicateOnKeydownButton} from '@github-ui/code-view-shared/components/DuplicateOnKeydownButton'
import {useShortcut} from '@github-ui/code-view-shared/hooks/shortcuts'
import {useReposAnalytics} from '@github-ui/code-view-shared/hooks/use-repos-analytics'
import type {PanelType} from '@github-ui/code-view-types'
import {Link} from '@primer/react'
import {clsx} from 'clsx'
import React, {lazy, Suspense, useEffect} from 'react'

import {useFindInFileOpen} from '../../../contexts/FindInFileOpenContext'
import {useSetSearchTerm} from '../../../contexts/SearchTermContext'
import {useCurrentBlame} from '../../../hooks/CurrentBlame'
import {useCurrentBlob} from '../../../hooks/CurrentBlob'
import {useCurrentStyledBlob} from '../../../hooks/CurrentStyledBlob'
import {BlobDisplayType, useBlobRendererType} from '../../../hooks/use-blob-renderer-type'
import {useIsCursorEnabled} from '../../../hooks/use-cursor-navigation'
import type {SetStickyLinesType} from '../../../hooks/use-sticky-lines'
import {assertNever} from '../../../utilities/assert-never'
import {textAreaId} from '../../../utilities/lines'
import {MarkdownContent} from '../../MarkdownContent'
import {LoadingFallback} from '../../SuspenseFallback'
import styles from './BlobContent.module.css'
import type {CodeLinesHandle} from './Code/code-lines-handle'
import {CodeBlob} from './Code/CodeBlob'
import {ImageBlob} from './Renderable/ImageBlob'
import {YamlTemplateContent, YamlTemplateType} from './Renderable/YamlTemplateContent'

const CSVBlob = lazy(() => import('./CSV/CSVBlob'))
const FileRendererBlob = lazy(() => import('@github-ui/file-renderer-blob'))

export interface CodeNavData {
  selectedText: string
  lineNumber: number
  offset: number
}

export function BlobContent({
  setOpenPanel,
  codeNavInfo,
  validCodeNav,
  onCodeNavTokenSelected,
  onLineStickOrUnstick,
  searchResults,
  blobLinesHandle,
  focusedSearchResult,
}: {
  setOpenPanel: (panel: PanelType | undefined) => void
  codeNavInfo: CodeNavigationInfo | undefined
  validCodeNav: boolean
  onCodeNavTokenSelected: (codeNavData: CodeNavData) => void
  onLineStickOrUnstick: SetStickyLinesType
  searchResults: DefinitionOrReference[]
  blobLinesHandle: React.RefObject<CodeLinesHandle | null>
  focusedSearchResult: number | undefined
}) {
  const hasBlame = !!useCurrentBlame()
  const {rawLines} = useCurrentStyledBlob()
  const {sendRepoKeyDownEvent} = useReposAnalytics()
  const {findInFileShortcut} = useShortcut()
  const setSearchTerm = useSetSearchTerm()

  const blobRendererType = useBlobRendererType()
  const {setFindInFileOpen} = useFindInFileOpen()
  const isRenderingCode = blobRendererType === BlobDisplayType.Code

  // Find in file
  const findInFileHotkey = isRenderingCode && rawLines != null && validCodeNav ? findInFileShortcut.hotkey : ''
  const cursorEnabled = useIsCursorEnabled()

  useEffect(() => {
    if (!isRenderingCode) {
      setOpenPanel(undefined)
    }
  }, [isRenderingCode, setOpenPanel])

  function onHotkeyPressed() {
    setFindInFileOpen(true)
    sendRepoKeyDownEvent('BLOB_FIND_IN_FILE_MENU.OPEN')
    const selection = window.getSelection()?.toString()
    if (selection) {
      setSearchTerm(selection)
    }
  }
  // We can't have the overflow classes for these types because it interferes with the HighlightedLineMenu
  // For markdown, overflow: auto causes lag with code blocks
  const hasOverflow =
    !isRenderingCode && blobRendererType !== BlobDisplayType.CSV && blobRendererType !== BlobDisplayType.Markdown
  const isMarkdown = blobRendererType === BlobDisplayType.Markdown

  return (
    <section
      aria-labelledby="file-name-id-wide file-name-id-mobile"
      className={clsx(
        styles.blobContentSection,
        isMarkdown && styles.blobContentSectionMarkdown,
        hasOverflow && styles.blobContentSectionOverflow,
      )}
      style={{
        // 46px is the height of the header, and to fix the weirdness of the header pushing all content down, we need
        // to add padding to the body content of a given view. Otherwise the header covers up the first few lines of
        // any given view. 92px is the height of the blame header because there is an extra line with the color scheme
        marginTop: hasBlame ? '92px' : '46px',
      }}
    >
      <Blob
        blobLinesHandle={blobLinesHandle}
        onCodeNavTokenSelected={onCodeNavTokenSelected}
        codeSections={hasBlame ? undefined : codeNavInfo?.codeSections}
        codeLineToSectionMap={codeNavInfo ? codeNavInfo.lineToSectionMap : undefined}
        validCodeNav={validCodeNav}
        onLineStickOrUnstick={onLineStickOrUnstick}
        searchResults={searchResults}
        focusedSearchResult={focusedSearchResult}
      />
      {isRenderingCode && !cursorEnabled && (
        <DuplicateOnKeydownButton
          buttonFocusId={textAreaId}
          buttonHotkey={findInFileHotkey}
          onButtonClick={onHotkeyPressed}
          buttonTestLabel={'hotkey-button'}
        />
      )}
    </section>
  )
}

const Blob = React.memo(function Blob({
  onCodeNavTokenSelected,
  codeSections,
  codeLineToSectionMap,
  validCodeNav,
  onLineStickOrUnstick,
  searchResults,
  blobLinesHandle,
  focusedSearchResult,
}: {
  onCodeNavTokenSelected: (codeNavData: CodeNavData) => void
  codeSections?: Map<number, CodeSection[]>
  codeLineToSectionMap?: CodeSectionLookup
  validCodeNav: boolean
  onLineStickOrUnstick: SetStickyLinesType
  searchResults: DefinitionOrReference[]
  blobLinesHandle: React.RefObject<CodeLinesHandle | null>
  focusedSearchResult: number | undefined
}) {
  const payload = useCurrentBlob()

  const displayType = useBlobRendererType()

  switch (displayType) {
    case BlobDisplayType.TooLargeError:
      return (
        <div className={styles.tooLargeError} data-hpc>
          <Link href={payload.rawBlobUrl}>View raw</Link>
          {payload.large && <p>(Sorry about that, but we can’t show files that are this big right now.)</p>}
        </div>
      )
    case BlobDisplayType.Code:
      return (
        <CodeBlob
          blobLinesHandle={blobLinesHandle}
          onCodeNavTokenSelected={onCodeNavTokenSelected}
          codeSections={codeSections}
          codeLineToSectionMap={codeLineToSectionMap}
          validCodeNav={validCodeNav}
          onLineStickOrUnstick={onLineStickOrUnstick}
          searchResults={searchResults}
          focusedSearchResult={focusedSearchResult}
        />
      )
    case BlobDisplayType.Markdown:
      return <MarkdownContent richText={payload.richText!} className={styles.markdownBlob} />
    case BlobDisplayType.CSV:
      return (
        <Suspense fallback={<LoadingFallback />}>
          <CSVBlob csv={payload.csv!} />
        </Suspense>
      )
    case BlobDisplayType.FileRenderer:
      return (
        <Suspense fallback={<LoadingFallback />}>
          <FileRendererBlob
            key={payload.renderedFileInfo!.identityUUID}
            identityUuid={payload.renderedFileInfo!.identityUUID}
            size={payload.renderedFileInfo!.size}
            type={payload.renderedFileInfo!.renderFileType}
            url={payload.displayUrl}
          />
        </Suspense>
      )
    case BlobDisplayType.Image:
      return <ImageBlob displayName={payload.displayName} displayUrl={payload.displayUrl} />
    case BlobDisplayType.IssueTemplate:
      return (
        <YamlTemplateContent
          issueTemplate={payload.issueTemplate ? payload.issueTemplate : payload.discussionTemplate!}
          type={payload.issueTemplate ? YamlTemplateType.Issue : YamlTemplateType.Discussion}
          data-hpc
        />
      )
    default:
      assertNever(displayType)
  }
})
